import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Upload, Link as LinkIcon, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: '',
  price: '',
  image: '',
  category: 'couvertines',
  color: '',
  finish: '',
  description: '',
  stock: 100,
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageMode, setImageMode] = useState('upload'); // 'upload' | 'url'
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/products`);
      setProducts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setImageMode('upload');
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      price: p.price,
      image: p.image,
      category: p.category,
      color: p.color || '',
      finish: p.finish || '',
      description: p.description || '',
      stock: p.stock ?? 100,
    });
    // If existing image is a public storage URL (starts with /api/files/), default to upload mode preview, otherwise URL mode
    setImageMode(p.image && p.image.includes('/api/files/') ? 'upload' : 'url');
    setShowModal(true);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Format non supporté (JPG, PNG, WEBP, GIF)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (5 Mo max)');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/admin/upload-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success('Image téléversée');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image) {
      toast.error('Image requise (téléversez un fichier ou collez une URL)');
      return;
    }
    const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock) };
    try {
      if (editing) {
        await axios.put(`${API}/admin/products/${editing._id}`, payload);
        toast.success('Produit modifié');
      } else {
        await axios.post(`${API}/admin/products`, payload);
        toast.success('Produit créé');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer "${p.name}" ?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p._id}`);
      toast.success('Produit supprimé');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Produits</h1>
          <p className="text-gray-500 mt-1">{products.length} produit(s)</p>
        </div>
        <button
          data-testid="admin-create-product-btn"
          onClick={openCreate}
          className="bg-[#E42313] text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-[#C11A0F] transition-colors"
        >
          <Plus className="w-4 h-4" /> Nouveau produit
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500">Chargement...</div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-700">Image</th>
                <th className="text-left p-4 font-semibold text-gray-700">Nom</th>
                <th className="text-left p-4 font-semibold text-gray-700">Catégorie</th>
                <th className="text-right p-4 font-semibold text-gray-700">Prix</th>
                <th className="text-center p-4 font-semibold text-gray-700">Stock</th>
                <th className="text-right p-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="admin-products-table">
              {products.map((p) => (
                <tr key={p._id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-14 h-14 object-cover rounded"
                    />
                  </td>
                  <td className="p-4 font-medium text-gray-900">{p.name}</td>
                  <td className="p-4 text-gray-600 capitalize">{p.category}</td>
                  <td className="p-4 text-right font-semibold">{p.price.toFixed(2)} €</td>
                  <td className="p-4 text-center">{p.stock}</td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => openEdit(p)}
                      className="p-2 text-gray-600 hover:text-[#E42313] inline-flex"
                      data-testid={`edit-product-${p._id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => remove(p)}
                      className="p-2 text-gray-600 hover:text-red-600 inline-flex"
                      data-testid={`delete-product-${p._id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold">
                {editing ? 'Modifier le produit' : 'Nouveau produit'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nom</label>
                <input
                  data-testid="product-form-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:border-[#E42313] focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Prix (€)</label>
                  <input
                    data-testid="product-form-price"
                    type="number"
                    step="0.01"
                    required
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="w-full px-3 py-2 border rounded focus:border-[#E42313] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Stock</label>
                  <input
                    type="number"
                    value={form.stock}
                    onChange={(e) => setForm({ ...form, stock: e.target.value })}
                    className="w-full px-3 py-2 border rounded focus:border-[#E42313] focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Catégorie</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:border-[#E42313] focus:outline-none"
                >
                  <option value="couvertines">Couvertines</option>
                  <option value="tablettes">Tablettes</option>
                  <option value="habillages">Habillages</option>
                  <option value="accessoires">Accessoires</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Image du produit</label>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    data-testid="image-mode-upload"
                    onClick={() => setImageMode('upload')}
                    className={`px-3 py-1.5 text-sm rounded inline-flex items-center gap-1.5 border ${imageMode === 'upload' ? 'bg-[#E42313] text-white border-[#E42313]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  >
                    <Upload className="w-3.5 h-3.5" /> Téléverser un fichier
                  </button>
                  <button
                    type="button"
                    data-testid="image-mode-url"
                    onClick={() => setImageMode('url')}
                    className={`px-3 py-1.5 text-sm rounded inline-flex items-center gap-1.5 border ${imageMode === 'url' ? 'bg-[#E42313] text-white border-[#E42313]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  >
                    <LinkIcon className="w-3.5 h-3.5" /> Coller une URL
                  </button>
                </div>

                {imageMode === 'upload' ? (
                  <div className="space-y-2">
                    <input
                      ref={fileInputRef}
                      data-testid="product-form-image-file"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleFileSelect}
                      disabled={uploading}
                      className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 file:cursor-pointer disabled:opacity-50"
                    />
                    <p className="text-xs text-gray-500">JPG, PNG, WEBP ou GIF — 5 Mo max.</p>
                    {uploading && <p className="text-xs text-[#E42313]">Téléversement en cours...</p>}
                  </div>
                ) : (
                  <input
                    data-testid="product-form-image"
                    placeholder="https://..."
                    value={form.image}
                    onChange={(e) => setForm({ ...form, image: e.target.value })}
                    className="w-full px-3 py-2 border rounded focus:border-[#E42313] focus:outline-none"
                  />
                )}

                {form.image ? (
                  <div className="mt-3 flex items-center gap-3">
                    <img
                      src={form.image}
                      alt="Aperçu"
                      data-testid="product-form-image-preview"
                      className="w-20 h-20 object-cover rounded border border-gray-200"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <div className="text-xs text-gray-500 break-all flex-1">{form.image}</div>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, image: '' })}
                      className="text-xs text-gray-500 hover:text-red-600 inline-flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Retirer
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 w-20 h-20 rounded border border-dashed border-gray-300 flex items-center justify-center text-gray-300">
                    <ImageIcon className="w-6 h-6" />
                  </div>
                )}
                {/* hidden input keeps form.image required for submission */}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Couleur</label>
                  <input
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="w-full px-3 py-2 border rounded focus:border-[#E42313] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Finition</label>
                  <input
                    value={form.finish}
                    onChange={(e) => setForm({ ...form, finish: e.target.value })}
                    className="w-full px-3 py-2 border rounded focus:border-[#E42313] focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:border-[#E42313] focus:outline-none"
                />
              </div>
              <div className="flex gap-3 justify-end pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  data-testid="product-form-submit"
                  type="submit"
                  className="px-6 py-2 bg-[#E42313] text-white rounded hover:bg-[#C11A0F]"
                >
                  {editing ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
